import { Group } from 'fabric';
import { assembleSpecs } from '../assemble';
import { digitColumns } from '../kits/stackKit';
import { estimateMeasure } from '../kits/specs';
import type { PrimitiveSpec, TextMeasurer, TextSpec } from '../kits/specs';
import { registerRecipe } from '../registry';
import { tagSmartObject } from '../types';
import type { ToolRecipe } from '../types';

/**
 * Column addition / subtraction (CATALOG M1a) — 2–3 numbers stacked and
 * right-aligned over a rule, with an operator on the left, optional carry /
 * borrow boxes above and optional answer boxes below. Built on
 * stackKit.digitColumns (THE UNITS-COLUMN LAW: every row right-aligns into one
 * column grid, so ones sit under ones and the per-column boxes line up by
 * construction).
 *
 * WHY THE NUMBERS ARE RENDERED HERE, NOT BY THE KIT.
 * digitColumns emits one text cell PER CHARACTER — perfect for alignment, but
 * a per-character cell can't be double-click edited as a whole number (the
 * engine's write-back, buildTableCellParams {kind:'param'}, replaces the
 * cell's entire text with the new param value). So this recipe uses
 * digitColumns for the GEOMETRY — column centers, carry/answer boxes, the rule
 * and the per-row baselines — but renders each number as a SINGLE
 * param-tagged, right-aligned text. Because digit glyphs are tabular
 * (fixed-advance) in ordinary faces, a right-aligned number's digits land
 * exactly on the kit's column centers when the column pitch is set to one
 * digit advance (colWidth = measure('0')). Editing a number therefore re-lays
 * the columns and keeps everything aligned.
 *
 * THE CARRY COLUMN. An addition can carry into a new place (47 + 85 = 132), so
 * when the carry or answer boxes are on for '+', one extra leading column is
 * added — otherwise the final carry / hundreds digit would have nowhere to go.
 * Subtraction never grows a column, so it gets none.
 *
 * op is coerced to '+' or '−' (true minus, U+2212). Numbers are param-tagged;
 * the operator is set from the form. Emits Rect + Line + FabricText only.
 */

// ---------------------------------------------------------------------------
// Defensive param coercion (the fraction.ts/handwriting.ts pattern).
// ---------------------------------------------------------------------------

function str(v: unknown, fallback: string): string {
  return typeof v === 'string' && v.trim() !== '' ? v : fallback;
}

/** Unlike str(), an empty string is a MEANINGFUL value (= row not used). */
function rawStr(v: unknown, fallback: string): string {
  return typeof v === 'string' ? v : fallback;
}

function bool(v: unknown, fallback: boolean): boolean {
  if (typeof v === 'boolean') return v;
  if (v === 'true') return true;
  if (v === 'false') return false;
  return fallback;
}

// ---------------------------------------------------------------------------
// Text measurement — canvas TextMetrics when available, estimate fallback.
// ---------------------------------------------------------------------------

function makeMeasurer(family: string): TextMeasurer {
  try {
    const ctx = document.createElement('canvas').getContext('2d');
    if (!ctx) return estimateMeasure;
    return (text, fontSize) => {
      ctx.font = `${fontSize}px "${family}", sans-serif`;
      const w = ctx.measureText(text).width;
      return Number.isFinite(w) && w >= 0 ? w : estimateMeasure(text, fontSize);
    };
  } catch {
    return estimateMeasure;
  }
}

// ---------------------------------------------------------------------------
// Recipe
// ---------------------------------------------------------------------------

const INK = '#1e293b';
const DEFAULT_FONT = 'Arial';
const FONT_SIZE = 120;

/** '−' is the true minus sign (U+2212); a hyphen reads as too short in print. */
const MINUS = '−';

const columnMath: ToolRecipe = {
  id: 'column-math',
  domain: 'math',
  tags: ['addition', 'subtraction', 'arithmetic', 'column-math', 'regrouping', 'worksheet'],
  name: 'Column addition',
  category: 'math',
  version: 1,
  description:
    'Stacked column addition or subtraction — numbers right-aligned over a rule, with optional carry boxes on top and answer boxes below. Double-click a number to change the problem.',
  defaults: {
    n1: '47',
    n2: '85',
    n3: '',
    op: '+',
    carrySlots: true,
    answerBoxes: true,
    color: INK,
    fontFamily: DEFAULT_FONT,
  },
  schema: [
    { key: 'n1', label: 'First number', type: 'text' },
    { key: 'n2', label: 'Second number', type: 'text' },
    {
      key: 'n3',
      label: 'Third number',
      type: 'text',
      help: 'Optional — leave empty for a two-number problem.',
    },
    {
      key: 'op',
      label: 'Operation',
      type: 'select',
      options: [
        { value: '+', label: 'Add (+)' },
        { value: MINUS, label: 'Subtract (−)' },
      ],
    },
    {
      key: 'carrySlots',
      label: 'Carry / borrow boxes',
      type: 'boolean',
      help: 'Dashed boxes on top to write carries or borrows.',
    },
    {
      key: 'answerBoxes',
      label: 'Answer boxes',
      type: 'boolean',
      help: 'Empty boxes below the line for the answer.',
    },
    { key: 'color', label: 'Color', type: 'color', section: 'Style' },
    { key: 'fontFamily', label: 'Font', type: 'font', section: 'Style' },
  ],
  async generate(params) {
    const opRaw = str(params.op, '+');
    const op = opRaw === MINUS || opRaw === '-' ? MINUS : '+';
    const color = str(params.color, INK);
    const family = str(params.fontFamily, DEFAULT_FONT);
    const carrySlots = bool(params.carrySlots, true);
    const answerBoxes = bool(params.answerBoxes, true);
    const measure = makeMeasurer(family);

    // 2–3 number rows, each keyed so it can carry its own dblclick param tag.
    const candidates = [
      { text: rawStr(params.n1, ''), param: 'n1' },
      { text: rawStr(params.n2, ''), param: 'n2' },
      { text: rawStr(params.n3, ''), param: 'n3' },
    ].filter((r) => r.text.length > 0);
    // A column problem needs at least two rows; fall back rather than emit a
    // degenerate single-row object.
    const rows =
      candidates.length >= 2
        ? candidates
        : [
            { text: '0', param: 'n1' },
            { text: '0', param: 'n2' },
          ];

    // Tight column pitch = one digit advance, so a right-aligned whole-number
    // text lands its (tabular) digits exactly on the kit's column centers.
    const digitAdvance = measure('0', FONT_SIZE) || FONT_SIZE * 0.55;

    // Room for a final carry: addition can grow one place when carry/answer
    // boxes are shown. Prefix every row the kit sees with the spacer columns
    // (skipped, glyph-free) so the grid — and its boxes — gains the column.
    const extraCols = op === '+' && (carrySlots || answerBoxes) ? 1 : 0;
    const spacer = ' '.repeat(extraCols);

    const block = digitColumns({
      rows: rows.map((r) => spacer + r.text),
      fontSize: FONT_SIZE,
      color,
      fontFamily: family,
      measure,
      colWidth: digitAdvance,
      carrySlots,
      answerBoxes,
      rule: true,
    });

    // Per-row baselines: the distinct y of the kit's (now-discarded) digit
    // cells, top to bottom — one per number row, carry-slot offset included.
    const rowYs = [
      ...new Set(
        block.specs
          .filter((s): s is TextSpec => s.kind === 'text')
          .map((s) => s.y),
      ),
    ].sort((a, b) => a - b);

    // Keep the kit's structure (carry rects, rule, answer boxes); drop its
    // per-character text cells and render editable whole numbers instead.
    const specs: PrimitiveSpec[] = block.specs.filter((s) => s.kind !== 'text');

    rows.forEach((r, i) => {
      specs.push({
        kind: 'text',
        text: r.text,
        x: block.width,
        y: rowYs[i] ?? rowYs[rowYs.length - 1] ?? 0,
        fontSize: FONT_SIZE,
        fontFamily: family,
        fill: color,
        originX: 'right',
        originY: 'top',
        param: r.param,
      });
    });

    // Operator: to the left of the columns, on the last row's baseline.
    specs.push({
      kind: 'text',
      text: op,
      x: -digitAdvance * 0.4,
      y: rowYs[rowYs.length - 1] ?? 0,
      fontSize: FONT_SIZE,
      fontFamily: family,
      fill: color,
      originX: 'right',
      originY: 'top',
    });

    // Children carry absolute coords; Group derives the bbox and the caller
    // places it (mirrors every other recipe).
    const group = new Group(assembleSpecs(specs));
    group.set({ left: 0, top: 0 });
    return tagSmartObject(group, columnMath, params);
  },
};

registerRecipe(columnMath);
